// After a deploy, a tab opened on the previous release may ask for a code file (a lazy page) that is gone (the
// server keeps previous releases' files for a while: http/assetArchive.ts; this is the fallback past that). Only the
// public marketing pages reload by themselves, once per build per tab (guarded in tab storage: never a loop). The
// dashboard, admin and the shopper's personalizer never reload by themselves (unsaved work, a shopper's photo or
// art): they show a notice with a reload button instead.

const RELOAD_KEY = 'perzo:chunk-reload';
const NOTICE_ID = 'perzo-refresh-notice';
export const NOTICE_TEXT = 'A new version of Perzo is available — reload when you’re ready.';

/** Whether an error is a code file of this app that could not be loaded (Vite preload, dynamic import). */
export function isChunkLoadError(err: unknown): boolean {
  const message = err instanceof Error ? err.message : typeof err === 'string' ? err : '';
  return /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Unable to preload CSS|Loading chunk \S+ failed|Loading CSS chunk/i.test(message);
}

export interface RecoveryEnv {
  /** A public marketing page (nothing to lose): may reload by itself. Never the dashboard, admin or personalizer. */
  autoReload: boolean;
  /** This document's build (its entry file): one automatic reload per build per tab. */
  build: string;
  storage: Pick<Storage, 'getItem' | 'setItem'> | null;
  reload: () => void;
  notice: () => void;
}

/** Builds remembered per tab (the oldest are forgotten past this; a tab never sees that many deploys). */
const MAX_REMEMBERED = 20;

/**
 * What to do about a missing code file: on the public site, one reload per build per tab (every build already
 * reloaded is remembered, so going back to one, A → B → A, never reloads it again; once MAX_REMEMBERED builds have
 * reloaded, the tab reloads no more, nothing is forgotten); otherwise the reload notice.
 */
export function recoverFromChunkError(env: RecoveryEnv): 'reload' | 'notice' {
  if (env.autoReload && env.storage) {
    let done: string[] | null = null;
    try {
      const parsed = JSON.parse(env.storage.getItem(RELOAD_KEY) ?? '[]') as unknown;
      done = Array.isArray(parsed) ? parsed.filter((b): b is string => typeof b === 'string') : [];
    } catch {
      done = null;
    }
    if (done && done.length < MAX_REMEMBERED && !done.includes(env.build)) {
      try {
        env.storage.setItem(RELOAD_KEY, JSON.stringify([...done, env.build]));
        env.reload();
        return 'reload';
      } catch {
        // Without tab storage the guard cannot be kept: no automatic reload.
      }
    }
  }
  env.notice();
  return 'notice';
}

/** A small, non-blocking notice; the page reloads only when the person presses the button. */
export function showRefreshNotice(doc: Document = document): void {
  if (doc.getElementById(NOTICE_ID)) return;
  const box = doc.createElement('div');
  box.id = NOTICE_ID;
  box.setAttribute('role', 'status');
  box.style.cssText =
    'position:fixed;left:16px;right:16px;bottom:16px;z-index:2147483647;max-width:480px;margin:0 auto;padding:12px 16px;border-radius:8px;background:#111;color:#fff;font:14px/1.4 system-ui,sans-serif;display:flex;gap:12px;align-items:center;justify-content:space-between;box-shadow:0 4px 16px rgba(0,0,0,.25)';
  const text = doc.createElement('span');
  text.textContent = NOTICE_TEXT;
  const button = doc.createElement('button');
  button.type = 'button';
  button.textContent = 'Reload';
  button.style.cssText = 'flex:none;padding:6px 12px;border:0;border-radius:6px;background:#fff;color:#111;font:inherit;cursor:pointer';
  button.addEventListener('click', () => doc.defaultView?.location.reload());
  box.append(text, button);
  doc.body.appendChild(box);
}

/**
 * Wires the recovery for this document (main.tsx). `autoReload`: asked at the time of the error (the page may have
 * changed by then). Returns the handler for React's uncaught render errors.
 */
export function installChunkRecovery(opts: { build: string; autoReload: () => boolean }): (error: unknown) => boolean {
  const handle = (): 'reload' | 'notice' => {
    let storage: Storage | null = null;
    try {
      storage = window.sessionStorage;
    } catch {
      storage = null;
    }
    return recoverFromChunkError({ autoReload: opts.autoReload(), build: opts.build, storage, reload: () => window.location.reload(), notice: () => showRefreshNotice() });
  };
  // Vite's own event for a lazy file (or its preloads) that failed to load. Prevented only when reloading: otherwise
  // the error goes on to the page (which keeps its state) under the notice.
  window.addEventListener('vite:preloadError', (event) => {
    if (handle() === 'reload') event.preventDefault();
  });
  return (error: unknown) => {
    if (!isChunkLoadError(error)) return false;
    handle();
    return true;
  };
}
